import {
  Button,
  DatePicker,
  Image,
  Input,
  message,
  Modal,
  Popconfirm,
  Popover,
  Progress,
  Radio,
  Spin,
  Table,
  TableProps,
} from "antd";
import "./consultation_record_style.css";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ConsultationRecordApi from "../../apis/consultationRecordApi";
import moment from "moment";
import dayjs from "dayjs";
import { QuestionCircleOutlined } from "@ant-design/icons";
import React from "react";

const { RangePicker } = DatePicker;

interface DataType {
  recordId: string;
  avatar: string;
  nickName: string;
  createTime: string;
  name: string;
  gender: "man" | "women";
  age: number;
  symptomDescription: string;
  location: string;
  state: number;
  prescriptionStatus: string;
  prescriptionTime: string;
  revisitStatus: string;
  doctorName: string;
  lastRecordId: string;
  rate: number;
  templateName: string;
  contactWay: string;
}

interface UserInfo {
  accountId: string;
  name: string;
  avatar: string;
  telphone: string;
  data: object;
}

let timerConst = 0;

interface LastSymptom {
  symptomDescription: string;
  tongueImage: string;
}

const RevisitTag: React.FC<{ lastRecordId: string }> = ({ lastRecordId }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [symptomData, setSymptomData] = useState<LastSymptom | null>(null);

  const handleOpenChange = (visible: boolean) => {
    setOpen(visible);
    if (visible && !symptomData) {
      setLoading(true);
      ConsultationRecordApi.getLastSymptom(lastRecordId)
        .then((res) => {
          setSymptomData(res.data);
        })
        .finally(() => setLoading(false));
    }
  };

  const content = loading ? (
    <div style={{ padding: "16px 32px", textAlign: "center" }}>
      <Spin size="small" />
    </div>
  ) : (
    <div style={{ maxWidth: 320 }}>
      <div style={{ marginBottom: 8 }}>
        <span style={{ fontWeight: 500 }}>上次就诊主诉：</span>
        <span>{symptomData?.symptomDescription || "-"}</span>
      </div>
      {symptomData?.tongueImage && (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>舌苔、舌底照片：</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {symptomData.tongueImage.split(",").map((url, idx) => (
              <Image
                key={idx}
                width={80}
                height={80}
                src={url.trim()}
                style={{ objectFit: "cover", borderRadius: 4 }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      title="上次就诊信息"
      open={open}
      onOpenChange={handleOpenChange}
      trigger="hover"
    >
      <span style={{ color: "#1677ff", marginLeft: 4, cursor: "pointer" }}>
        （复诊）
      </span>
    </Popover>
  );
};

const ConsultationRecord: React.FC = () => {
  const userInfo: UserInfo = JSON.parse(sessionStorage.getItem("userInfo") ?? '');
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<DataType[]>([]);
  const [messageApi, contextHolder] = message.useMessage();
  // 诊断状态
  const [state, setState] = useState<number | undefined>();
  // 复诊状态
  const [revisitStatus, setRevisitStatus] = useState<string | undefined>(() => {
    const params = new URLSearchParams(location.search);
    return params.get("revisitStatus") ?? undefined;
  });
  // 患者名称
  const [patientName, setPatientName] = useState<string>("");
  // 开始日期
  const [startTime, setStartTime] = useState<string | null>(null);
  // 结束日期
  const [endTime, setEndTime] = useState<string | null>(null);
  // 页码
  const [pageNumber, setPageNumber] = useState<number>(1);
  // 数据总数
  const [total, setTotal] = useState<number>(0);
  // 开药弹窗
  const [prescriptionModal, setPrescriptionModal] = useState<{ open: boolean; recordId: string }>({ open: false, recordId: "" });
  const [prescriptionTime, setPrescriptionTime] = useState<string>(dayjs().format("YYYY-MM-DD HH:mm:ss"));

  const columns: TableProps<DataType>["columns"] = [
    {
      title: "微信信息",
      key: "wechatInfo",
      align: "center" as const,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Image width={40} height={40} src={record.avatar} style={{ borderRadius: 4 }} />
          <span>{record.nickName}</span>
        </div>
      ),
    },
    {
      title: "姓名",
      dataIndex: "name",
      key: "name",
      align: "center" as const,
      render: (_, record) => (
        <span>
          {record.name}
          {record.lastRecordId && (
            <RevisitTag lastRecordId={record.lastRecordId} />
          )}
        </span>
      ),
    },
    {
      title: "性别",
      dataIndex: "gender",
      key: "gender",
      align: "center" as const,
      render: (_, record) => (
        <span>{record.gender === "man" ? "男" : "女"}</span>
      ),
    },
    {
      title: "年龄",
      dataIndex: "age",
      key: "age",
      align: "center" as const,
    },
    {
      title: "联系方式",
      dataIndex: "contactWay",
      key: "contactWay",
      align: "center" as const,
    },    
    {
      title: "所属地",
      dataIndex: "location",
      key: "location",
      align: "center" as const,
    },
    {
      title: "填写进度",
      dataIndex: "rate",
      key: "rate",
      align: "center" as const,
      render: (_, record) => <Progress percent={record.rate * 100} />,
    },
    {
      title: "诊断状态",
      dataIndex: "state",
      key: "state",
      align: "center" as const,
      render: (_, record) => returnState(record.state),
    },
    {
      title: "开药状态",
      dataIndex: "prescriptionStatus",
      key: "prescriptionStatus",
      align: "center" as const,
      render: (_, record) =>
        record.prescriptionStatus === "ISSUED" ? (
          <span>{record.prescriptionTime ?? "-"}</span>
        ) : (
          <span>-</span>
        ),
    },
    {
      title: "复诊状态",
      dataIndex: "revisitStatus",
      key: "revisitStatus",
      align: "center" as const,
      render: (_, record) => {
        if (record.revisitStatus === "COMPLETED") return <span style={{ color: "#52c41a" }}>已完成</span>;
        if (record.revisitStatus === "PENDING") return <span style={{ color: "#faad14" }}>待复诊</span>;
        return <span>-</span>;
      },
    },
    {
      title: "问诊模版",
      dataIndex: "templateName",
      key: "templateName",
      align: "center" as const,
    },
    {
      title: "填写日期",
      dataIndex: "createTime",
      key: "createTime",
      align: "center" as const,
      render: (_, record) => (
        <span>
          {record.createTime}
          <span style={{ color: "#999", marginLeft: 6 }}>
            （{timeAgo(new Date(record.createTime).getTime())}）
          </span>
        </span>
      ),
    },
    {
      title: "操作",
      key: "操作",
      align: "center" as const,
      render: (_, record) => (
        <div style={{ color: "#999" }}>
          <Button
            type="link"
            onClick={() =>
              navigate(
                `/home/consultation-details/${record.recordId}/${record.state}`
              )
            }
          >
            查看
          </Button>
          |
          <Button
            type="link"
            onClick={() => {
              setPrescriptionTime(dayjs().format("YYYY-MM-DD HH:mm:ss"));
              setPrescriptionModal({ open: true, recordId: record.recordId });
            }}
          >
            开药
          </Button>
          |
          <Popconfirm
            title="确定要发起复诊吗?"
            icon={<QuestionCircleOutlined style={{ color: "#1677ff" }} />}
            okText="确定"
            cancelText="取消"
            onConfirm={() => onIssueRevisit(record.recordId)}
          >
            <Button type="link">复诊</Button>
          </Popconfirm>
          |
          <Popconfirm
            title="确定要删除该记录吗?"
            icon={<QuestionCircleOutlined style={{ color: "red" }} />}
            okText="确定"
            cancelText="取消"
            onConfirm={() => onDelete(record.recordId)}
          >
            <Button type="link" style={{ color: "red" }}>
              删除
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const [timer, setTimer] = useState<number>(0);

  useEffect(() => {
    getRecord();
    setTimeout(() => {
      timerConst++;
      setTimer(timerConst);
    }, 5000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer]);

  // 返回时间差
  const timeAgo = (timestamp: number) => {
    const now = Math.floor(Date.now() / 1000);
    const diffSeconds = now - (timestamp / 1000);
 
    const timeUnits = [
        { unit: 31536000, text: '年' },
        { unit: 2592000, text: '月' },
        { unit: 86400, text: '天' },
        { unit: 3600, text: '小时' },
        { unit: 60, text: '分钟' },
        { unit: 1, text: '秒' }
    ];
 
    let timeAgoString = '';
    for (let i = 0; i < timeUnits.length; i++) {
        const unitInSeconds = timeUnits[i].unit;
        const unitText = timeUnits[i].text;
        const count = Math.floor(diffSeconds / unitInSeconds);
 
        if (count >= 1) {
            timeAgoString = `${count}${unitText}前`;
            break;
        }
    }
 
    return timeAgoString;
}

  // 返回状态
  const returnState = (state: number) => {
    if (state === 0) {
      return <span className="textBlueColor">填写中</span>;
    } else if (state === 1) {
      return <span className="themeFontColor">待诊断</span>;
    } else {
      return <span className="textAshColor">已诊断</span>;
    }
  };

  // 删除记录
  const onDelete = (id: string) => {
    ConsultationRecordApi.deleteRecord(id).then(() => {
      getRecord();
      messageApi.success(`删除成功`);
    });
  };

  // 开药
  const onIssuePrescription = () => {
    if (!prescriptionTime) {
      messageApi.error("请选择开药时间");
      return;
    }
    ConsultationRecordApi.issuePrescription(prescriptionModal.recordId, prescriptionTime).then(() => {
      setPrescriptionModal({ open: false, recordId: "" });
      getRecord();
      messageApi.success(`开药成功`);
    });
  };

  // 复诊
  const onIssueRevisit = (id: string) => {
    ConsultationRecordApi.issueRevisit(id).then(() => {
      getRecord();
      messageApi.success(`复诊发起成功`);
    });
  };

  // 获取今天日期
  const handleTodayClick = (value: string) => {
    let timer = moment();
    switch (value) {
      case "昨天":
        timer = timer.clone().subtract(1, "day").startOf("day");
        break;
      case "三天内":
        timer = timer.clone().subtract(3, "day").startOf("day");
        break;
      case "七天内":
        timer = timer.clone().subtract(7, "day").startOf("day");
        break;
    }
    const startOfDay = timer.clone().startOf("day");
    const endOfDay = moment().clone().endOf("day");

    const formattedStart = startOfDay.format("YYYY-MM-DD HH:mm:ss");
    const formattedEnd = endOfDay.format("YYYY-MM-DD HH:mm:ss");

    setStartTime(formattedStart);
    setEndTime(formattedEnd);
  };

  const getRecord = (number: number = 0) => {
    ConsultationRecordApi.getRecord(
      state,
      patientName,
      startTime,
      endTime,
      !!number ? number : pageNumber,
      userInfo.accountId,
      revisitStatus
    ).then((value) => {
      setTotal(value.data["total"]);
      setData(value.data["list"]);
      setLoading(false);
    });
  };

  return (
    <div className="consultation_record">
      {contextHolder}
      <h1>问诊记录</h1>
      <div>
        <div>
          <h4 className="flexBlockCenter">
            <Input
              placeholder="请输入患者姓名"
              allowClear
              suffix={
                <span className="themeFontColor" onClick={() => getRecord()}>
                  搜索
                </span>
              }
              onChange={(e) => setPatientName(e.target.value)}
            />

            <RangePicker
              style={{ marginLeft: 57 }}
              showTime={{ format: "HH:mm:ss" }}
              format="YYYY-MM-DD HH:mm:ss"
              value={[
                startTime !== null ? dayjs(startTime) : null,
                endTime !== null ? dayjs(endTime) : null,
              ]}
              onChange={(value, dateString) => {
                setStartTime(dateString[0] === "" ? null : dateString[0]);
                setEndTime(dateString[1] === "" ? null : dateString[1]);
              }}
            />
          </h4>
        </div>

        <div className="consultation_header">
          <h4>
            状态
            <Radio.Group
              style={{ marginLeft: 10 }}
              value={state}
              onChange={(e) => {
                setState(e.target.value);
              }}
            >
              {["填写中", "待诊断", "已诊断"].map(
                (name: string, index: number) => {
                  return (
                    <Radio.Button
                      key={index}
                      value={index}
                      onClick={() => {
                        if (index === state) setState(undefined);
                      }}
                    >
                      {name}
                    </Radio.Button>
                  );
                }
              )}
            </Radio.Group>
          </h4>

          <h4 className="select_timer">
            时间
            <Radio.Group
              style={{ marginLeft: 10 }}
              onChange={(e) => {
                handleTodayClick(e.target.value);
              }}
            >
              {["今天", "昨天", "三天内", "七天内"].map(
                (name: string, index: number) => {
                  return (
                    <Radio.Button key={index} value={name}>
                      {name}
                    </Radio.Button>
                  );
                }
              )}
            </Radio.Group>
          </h4>

          <h4 className="select_timer">
            复诊状态
            <Radio.Group
              style={{ marginLeft: 10 }}
              value={revisitStatus}
              onChange={(e) => setRevisitStatus(e.target.value)}
            >
              {[
                { label: "待复诊", value: "PENDING" },
                { label: "已完成", value: "COMPLETED" },
              ].map((item) => (
                <Radio.Button
                  key={item.value}
                  value={item.value}
                  onClick={() => {
                    if (item.value === revisitStatus) setRevisitStatus(undefined);
                  }}
                >
                  {item.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </h4>

          <h4 className="select_timer">
            <Button
              onClick={() => {
                setState(undefined);
                setRevisitStatus(undefined);
                setPatientName("");
                setStartTime(null);
                setEndTime(null);
                setPageNumber(1);
                setLoading(true);
                ConsultationRecordApi.getRecord(
                  undefined, "", null, null, 1, userInfo.accountId, undefined
                ).then((value) => {
                  setTotal(value.data["total"]);
                  setData(value.data["list"]);
                  setLoading(false);
                });
              }}
            >
              重置所有筛选
            </Button>
          </h4>
        </div>
      </div>

      <Table<DataType>
        columns={columns}
        loading={loading}
        dataSource={data}
        rowKey="recordId"
        pagination={{
          pageSize: 10,
          total: total,
          showSizeChanger: false,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (number) => {
            setPageNumber(number);
            getRecord(number);
          },
        }}
      />

      <Modal
        title="开药"
        open={prescriptionModal.open}
        okText="确定"
        cancelText="取消"
        onOk={onIssuePrescription}
        onCancel={() => setPrescriptionModal({ open: false, recordId: "" })}
      >
        <div style={{ padding: "16px 0" }}>
          <span style={{ marginRight: 8 }}>
            开药时间<span style={{ color: "red" }}>*</span>：
          </span>
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            value={prescriptionTime ? dayjs(prescriptionTime) : null}
            onChange={(_, dateString) =>
              setPrescriptionTime(dateString as string)
            }
          />
        </div>
      </Modal>
    </div>
  );
};

export default ConsultationRecord;
